import { useAccountCompanyId } from 'application/hooks';

import {
  LicenseType,
  OrganizationDiscountPureType,
  OrganizationDiscountStatus,
  PricePlanPeriodPureType,
  PricePlanPureType,
  useBillingCompanyBillingInfoQuery,
  useBillingOrganizationCurrentPricePlanQuery,
} from '@shared/api/graphql/generated';
import { FetchPolicyKeys } from '@shared/api/graphql/types';
import useSetOrganizationPricePlan from '@entities/pricePlan/api/useSetOrganizationPricePlan';

const SBER_PRIME_SLUG = 'sber_prime';

interface UseChangePricePlanPeriodModalViewModel {
  currentPricePlan: PricePlanPureType;
  currentPeriod: PricePlanPeriodPureType;
  isSubmitButtonLoading: boolean;
  isCancelButtonLoading: boolean;
  renewableLicensesCount: number;
  totalLicensesCount: number;
  isPrimeDiscountAvailable: boolean;
  hasInactiveLicenses: boolean;
  changePricePlanPeriod: (pricePlanPeriodId: string) => Promise<boolean>;
}

const useChangePricePlanPeriodModalViewModel = (): UseChangePricePlanPeriodModalViewModel => {
  const companyId = useAccountCompanyId();

  const { data: currentPricePlanData, loading: currentPricePlanLoading } = useBillingOrganizationCurrentPricePlanQuery({
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
    variables: { organizationId: companyId },
  });

  const { setPricePlanPeriodRequest, pricePlanPeriodData } = useSetOrganizationPricePlan();

  const { data: billingInfoData, loading: billingInfoLoading } = useBillingCompanyBillingInfoQuery({
    variables: {
      companyId,
    },
    fetchPolicy: FetchPolicyKeys.CACHE_AND_NETWORK,
  });

  const currentPricePlan = currentPricePlanData?.result?.pricePlan as PricePlanPureType;
  const currentPeriod = currentPricePlanData?.result?.pricePlanPeriod as PricePlanPeriodPureType;

  const discount = billingInfoData?.result?.discount as OrganizationDiscountPureType;
  const isPrimeDiscountAvailable =
    discount && discount.slug === SBER_PRIME_SLUG && discount.status === OrganizationDiscountStatus.InProgress;

  const renewableLicenses = (billingInfoData?.result?.renewableLicenses || []) as LicenseType[];
  const hasInactiveLicenses = Boolean(billingInfoData?.result?.hasInactiveLicenses);
  const totalLicenses = (billingInfoData?.result?.totalLicenses || []) as LicenseType[];

  const changePricePlanPeriod = async (pricePlanPeriodId: string) => {
    const isOk = await setPricePlanPeriodRequest(companyId, pricePlanPeriodId);

    return Boolean(isOk);
  };

  return {
    currentPricePlan,
    currentPeriod,
    renewableLicensesCount: Math.max(renewableLicenses.length, 1),
    totalLicensesCount: totalLicenses.length,
    isPrimeDiscountAvailable: Boolean(isPrimeDiscountAvailable),
    changePricePlanPeriod,
    isSubmitButtonLoading: currentPricePlanLoading || pricePlanPeriodData.loading || billingInfoLoading,
    isCancelButtonLoading: pricePlanPeriodData.loading,
    hasInactiveLicenses,
  };
};

export default useChangePricePlanPeriodModalViewModel;
